import type { Offer } from 'types/seller'

import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import copy from 'copy.json'
import { ArrowLeftIcon, CrossIcon } from 'icons'
import { adminPanelPaths } from 'paths'
import { useStore } from 'store'
import { addParamToPath, dimmBackground } from 'utils'

import Button from 'components/Button'
import ContentHeader from 'components/ContentHeader'
import Input from 'components/Input'
import PageContainer from 'components/PageContainer'
import Pagination from 'components/Pagination'
import Scroll from 'components/Scroll'
import Spinner from 'components/Spinner'
import Table from 'components/Table'
import IndividualEnergyPricingErrorModal from 'modals/IndividualEnergyPricingErrorModal'
import CsvFormatGuide from 'components/CsvFormatGuide'
import { individualPricingCsvGuide } from 'components/csvFormatGuides'
import OfferDetailsRow from './OfferDetailsRow'

type CalculateMatch = {
  offers_ids: number[]
  pricingFile?: File
}

export default function SearchOffersScreen() {
  const { api } = useStore()
  const { demandId, buyerId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [searchOffersData, setOffersData] = useState<Offer[] | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [chosenOffers, setChosenOffers] = useState<number[]>([])
  const [pricingFile, setPricingFile] = useState<File | null>(null)
  const [pricingFileErrorModalOpen, setPricingFileErrorModalOpen] = useState(false)
  const [individualPricingFileErrors, setIndividualPricingFileErrors] = useState<string[]>([])

  const form_text = copy.common.form
  const text = copy.page.search.offers

  useEffect(() => {
    const search = async () => {
      const response = await api.searchResources('offers', undefined, {
        page_number: currentPage,
        statuses: ['ACTIVE', 'RESERVED']
      })

      if (response) {
        setOffersData(response.content)
        setCurrentPage(response.number)
        setTotalPages(response.total_pages)
      }
    }

    search()
  }, [api, currentPage])

  const handlePageChange = (currentStep: number) => {
    setCurrentPage(currentStep - 1)
  }

  const handleChosenOffers = (offerId: number, checked: boolean) => {
    if (checked) {
      setChosenOffers(state => [...state, offerId])
    } else {
      setChosenOffers(state => state.filter(offer => offer !== offerId))
    }
  }

  const handleSend = async () => {
    setLoading(true)

    const requestData: CalculateMatch = {
      offers_ids: chosenOffers
    }

    delete requestData.pricingFile

    const formData = new FormData()

    formData.append(
      'request',
      new Blob([JSON.stringify(requestData)], { type: 'application/json' })
    )

    if (pricingFile) formData.append('pricingFile', pricingFile)

    const response = await api.calculateMatch(Number(demandId), formData)

    const isResponseError = !!response?.individual_energy_pricing_file_errors?.length

    if (isResponseError) {
      setIndividualPricingFileErrors(response?.individual_energy_pricing_file_errors)
      setPricingFileErrorModalOpen(true)
    }

    setLoading(false)

    if (response?.match_result?.match_id) {
      navigate(addParamToPath(adminPanelPaths.matches, response.match_result.match_id))
    }
  }

  const handleUncheckAll = () => {
    setChosenOffers([])
  }

  const resetInput = () => {
    const form = document.getElementById('resetInputForm') as HTMLFormElement
    form.reset()
    setPricingFile(null)
  }

  const handleFileChange = (event: FormEvent<HTMLInputElement> | null) => {
    const currentTarget = event?.currentTarget

    if (currentTarget?.files) {
      const file = currentTarget.files[0]

      setIndividualPricingFileErrors([])
      setPricingFile(file)

      return
    }

    resetInput()
  }

  useEffect(dimmBackground)

  return (
    <>
      <Content>
        <ContentHeader>
          <Link to={addParamToPath(adminPanelPaths.buyer.search, buyerId)}>
            <ArrowLeftIcon />
          </Link>

          <h1>{copy.page.search.heading_offers}</h1>
        </ContentHeader>

        <span>{copy.page.search.search_results}</span>

        <Scroll>
          <StyledTable>
            <thead>
              <tr>
                <th style={{ minWidth: 50 }} />
                <th>{form_text.status}</th>
                <th>{form_text.id}</th>
                <th>{form_text.energy_type}</th>
                <th>{form_text.distribution_name}</th>
                <th>{form_text.electric_energy_meter_number}</th>
                <th>{form_text.installation_power}</th>
                <th>{form_text.yearly_power_production}</th>
                <th>{form_text.energy_price}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {searchOffersData?.map(offer => (
                <OfferDetailsRow
                  key={offer.id}
                  offer={offer}
                  onChosenOffer={handleChosenOffers}
                  isOfferChosen={chosenOffers.includes(offer.id)}
                />
              ))}

              {!searchOffersData?.length && (
                <tr>
                  <td colSpan={10}>{text.not_found} </td>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </Scroll>

        {totalPages > 1 && (
          <StyledPagination
            numberOfSteps={totalPages}
            currentStep={currentPage + 1}
            onChange={handlePageChange}
          />
        )}
      </Content>

      <Footer>
        <Uncheck onClick={handleUncheckAll}>{text.uncheck_all}</Uncheck>

        <UploadFileForm id="resetInputForm">
          <label>{text.add_individual_energy_pricing}</label>
          <CsvFormatGuide definition={individualPricingCsvGuide} />
          <StyledInput name="file" type="file" onChange={handleFileChange} accept=".csv" />

          {pricingFile && (
            <ResetButton onClick={resetInput} type="button">
              <CrossIcon />
            </ResetButton>
          )}
        </UploadFileForm>

        <Summary>
          <p>
            {text.selected}: {chosenOffers.length} {text.from} {searchOffersData?.length}
          </p>

          <Button onClick={handleSend} disabled={chosenOffers.length < 1} style={{ minWidth: 160 }}>
            {loading && !pricingFileErrorModalOpen ? <Spinner size={16} /> : text.button_send}
          </Button>
        </Summary>
      </Footer>

      <IndividualEnergyPricingErrorModal
        isModalOpen={pricingFileErrorModalOpen}
        onModalClose={() => setPricingFileErrorModalOpen(false)}
        onHandleFileChange={handleFileChange}
        onModalContinue={handleSend}
        errors={individualPricingFileErrors}
        fileName={pricingFile?.name}
        loading={loading}
      />
    </>
  )
}

const Content = styled(PageContainer)`
  border-radius: 4px 4px 0 0;
  margin-bottom: 0;
`

const StyledTable = styled(Table)`
  .chosenOffer {
    background-color: var(--very-light-green);
  }
`

const StyledPagination = styled(Pagination)`
  align-self: center;
`

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;

  color: var(--white);
  background: var(--dark-gray);
  margin: 0px max(8px, calc((100% - 1312px) / 2));
  padding: 16px 24px;
  border-radius: 0 0 4px 4px;
`

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const Uncheck = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  font: var(--600-13);

  cursor: pointer;
`

const StyledInput = styled(Input)`
  background: transparent;
  outline-color: var(--green);
  padding-right: 25px;
`
const ResetButton = styled.button`
  background: transparent;
  border: none;
  color: var(--white);
  cursor: pointer;
`

const UploadFileForm = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;

  position: relative;

  > button {
    position: absolute;
    right: 3px;
  }
`
